/**
 * Tenant path prefix — `/t/<slug>/…` — read synchronously from the URL.
 *
 * Synchronous on purpose: the router needs its `basename` before it mounts, and
 * `createApiClient(tokenKey)` is called at module scope. Neither can await a
 * fetch, so the slug is parsed out of `location.pathname` and validated for
 * shape only. Whether that slug names a real tenant is the server's answer
 * (`TenantGate` → `GET /public/tenant-context`).
 *
 * See docs/FeatureDesigns/multi-tenancy/tenant-urls.md §6.
 */

/** Reserved first path segment for the path form. */
export const TENANT_PATH_SEGMENT = 't';

/** Shape check only — mirrors the server's slug rule enough to reject junk
 *  (`..`, encoded paths) without duplicating the full validator. */
const SLUG_SHAPE = /^[a-z0-9][a-z0-9-]{0,31}$/;

/**
 * Host labels that are never a tenant. Kept deliberately small: the check below
 * looks only at the first label and the label count, so it needs no list of
 * zones and keeps working when a zone is added.
 */
const SHARED_HOST_LABELS = new Set(['www', 'student', 'teacher', 'admin']);

export interface TenantPrefix {
    /** `basename` for the router: `/t/<slug>` or `/`. */
    basename: string;
    /** The slug taken from the path, if the URL used the path form. */
    slugFromPath?: string;
}

/**
 * Parse `/t/<slug>` off a pathname.
 *
 *   `/t/meru/home` → `{ basename: '/t/meru', slugFromPath: 'meru' }`
 *   `/home`        → `{ basename: '/' }`
 *   `/t`, `/t/`    → `{ basename: '/' }`  (no slug → not the path form)
 */
export function readTenantPrefix(pathname: string): TenantPrefix {
    const segments = pathname.split('/').filter(Boolean);
    if (segments[0] !== TENANT_PATH_SEGMENT) return { basename: '/' };

    const slug = (segments[1] ?? '').toLowerCase();
    if (!SLUG_SHAPE.test(slug)) return { basename: '/' };

    return { basename: `/${TENANT_PATH_SEGMENT}/${slug}`, slugFromPath: slug };
}

/**
 * Does this hostname look like it addresses a tenant?
 *
 * Used only to decide whether to *ask* the server, so that a plain
 * `buzzbrain.ai` load costs no extra request. Intentionally zone-agnostic —
 * fewer than three labels is an apex or `localhost`, and a known shared label is
 * a shared host. Anything else might be a tenant, and the server decides.
 */
export function hostSuggestsTenant(hostname: string): boolean {
    const labels = hostname.split('.');
    if (labels.length < 3) return false;
    return !SHARED_HOST_LABELS.has(labels[0].toLowerCase());
}

// ── Process-wide prefix ────────────────────────────────────────────────

/**
 * The prefix for this page load, computed once. The URL is the only source of
 * truth and it is re-read on every load — deliberately never cached in
 * `localStorage`, where a stored tenant outliving the URL that set it is a whole
 * class of "why am I still seeing their logo?" bug.
 */
const prefix: TenantPrefix = typeof window === 'undefined'
    ? { basename: '/' }
    : readTenantPrefix(window.location.pathname);

export function currentTenantPrefix(): TenantPrefix {
    return prefix;
}

/** The slug in the current URL's path, if any. */
export function tenantSlugFromPath(): string | undefined {
    return prefix.slugFromPath;
}

/**
 * Prefix an app-absolute path, for the few places that assign
 * `window.location` directly and so bypass the router's `basename`.
 * React-router `<Link>` / `navigate()` need no help.
 */
export function appHref(path: string): string {
    const suffix = path.startsWith('/') ? path : `/${path}`;
    return prefix.basename === '/' ? suffix : `${prefix.basename}${suffix}`;
}

/**
 * Namespace a `localStorage` session key by tenant when the path form is in use.
 *
 * On a shared host every tenant path shares one origin, so `/t/meru` and
 * `/t/teoler` would otherwise share one session slot. That is not a leak — the
 * token decides what its holder sees, and the shared host already keeps a single
 * session today — but it desyncs branding from the session and prevents holding
 * two tenant sessions at once. The host form needs nothing: separate origins
 * already give it separate storage.
 */
export function tenantTokenKey(baseKey: string): string {
    return tokenKeyForSlug(baseKey, prefix.slugFromPath);
}

/** The session slot `tenantTokenKey` would use on `/t/<slug>` (or on no prefix,
 *  for `undefined`) — for code about to send this page to another tenant path. */
export function tokenKeyForSlug(baseKey: string, slug: string | undefined): string {
    return slug ? `${baseKey}:${slug}` : baseKey;
}
